import React from 'react';
import { DRIVER_LABELS, DRIVER_ORDER } from '@/data/clubDemoData';
import {
  ArrowRight, TrendingUp, TrendingDown, Minus,
  AlertTriangle, Target, Zap, BarChart3,
  Globe, DollarSign, CheckCircle2,
} from 'lucide-react';

export const MR = { fontFamily: 'Manrope, sans-serif' };
export const JB = { fontFamily: "'JetBrains Mono', monospace" };

export function SectionTitle({ label, title, subtitle, as: Tag = 'h2' }) {
  return (
    <div className="mb-10">
      <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-primary mb-3" style={MR}>{label}</p>
      <Tag className="text-2xl md:text-3xl font-bold tracking-tight leading-tight mb-3" style={MR}>{title}</Tag>
      {subtitle && <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">{subtitle}</p>}
    </div>
  );
}

export function InsightBox({ children, variant = 'default' }) {
  const styles = variant === 'warning'
    ? 'border-[hsl(38,85%,55%)]/20 bg-[hsl(38,85%,55%)]/[0.04]'
    : variant === 'positive'
    ? 'border-[hsl(150,50%,45%)]/20 bg-[hsl(150,50%,45%)]/[0.04]'
    : 'border-primary/20 bg-primary/[0.04]';
  const icon = variant === 'warning'
    ? <AlertTriangle className="w-4 h-4 text-[hsl(38,85%,55%)] flex-shrink-0 mt-0.5" />
    : variant === 'positive'
    ? <Target className="w-4 h-4 text-[hsl(150,50%,45%)] flex-shrink-0 mt-0.5" />
    : <Zap className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />;
  return (
    <div className={`flex gap-3 p-4 rounded-xl border ${styles}`} data-testid="insight-box">
      {icon}
      <p className="text-sm text-foreground/90 leading-relaxed">{children}</p>
    </div>
  );
}

export function ScoreBar({ value, max = 100, color = 'bg-primary' }) {
  return (
    <div className="h-2 rounded-full bg-muted/40 overflow-hidden">
      <div className={`h-full rounded-full ${color} transition-all duration-700 ease-out`} style={{ width: `${(value / max) * 100}%` }} />
    </div>
  );
}

export function TrendBadge({ value }) {
  if (value > 0) return <span className="inline-flex items-center gap-1 text-xs font-medium text-[hsl(150,50%,45%)]"><TrendingUp className="w-3 h-3" /> +{value}%</span>;
  if (value < 0) return <span className="inline-flex items-center gap-1 text-xs font-medium text-[hsl(38,85%,55%)]"><TrendingDown className="w-3 h-3" /> {value}%</span>;
  return <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground"><Minus className="w-3 h-3" /> 0%</span>;
}

export function DriverCard({ driverKey, value, insight, label: labelProp }) {
  const label = labelProp || DRIVER_LABELS[driverKey];
  const color = value >= 75 ? 'bg-[hsl(150,50%,45%)]' : value >= 55 ? 'bg-primary' : 'bg-[hsl(38,85%,55%)]';
  const textColor = value >= 75 ? 'text-[hsl(150,50%,45%)]' : value >= 55 ? 'text-primary' : 'text-[hsl(38,85%,55%)]';

  return (
    <div className="rounded-xl border border-border/30 bg-card/40 p-5 space-y-3" data-testid={`driver-${driverKey}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground" style={MR}>{label}</span>
        <span className={`text-lg font-bold ${textColor}`} style={MR}>{value}</span>
      </div>
      <ScoreBar value={value} color={color} />
      <p className="text-xs text-muted-foreground leading-relaxed">{insight}</p>
    </div>
  );
}
